import {
  doc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  runTransaction,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import {
  TX_TYPES,
  calculateTier,
  calculateEarnPoints,
  getTierConfig,
  normalizeLoyalty,
  addMonthsToDate,
} from '../../../domain/loyaltyCalculations';
import { getLoyaltyConfig } from './loyaltyService';

const MAX_BACKFILL_BILLS = 240;

const parseBillDate = (date) => {
  if (!date) return null;
  if (date instanceof Timestamp) return date.toDate();
  if (typeof date?.toDate === 'function') return date.toDate();
  if (typeof date === 'string') {
    const d = new Date(date);
    return isNaN(d.getTime()) ? null : d;
  }
  if (date instanceof Date) return isNaN(date.getTime()) ? null : date;
  return null;
};

export const backfillCustomerPoints = async (customerId, employeeId = 'system', businessId) => {
  if (!customerId) throw new Error('customerId is required');
  if (!businessId) throw new Error('backfillCustomerPoints: businessId is required');

  const customerRef = doc(db, 'users', customerId);
  const preSnap = await getDoc(customerRef);
  if (!preSnap.exists()) {
    const e = new Error('Müşteri bulunamadı');
    e.code = 'customer-not-found';
    throw e;
  }
  const preLoyalty = normalizeLoyalty(preSnap.data().loyalty);
  if (preLoyalty.backfillCompletedAt) {
    const e = new Error('Bu müşteri için backfill zaten tamamlanmış');
    e.code = 'already-backfilled';
    throw e;
  }

  const billsQuery = query(
    collection(db, 'bills'),
    where('customerId', '==', customerId),
    where('status', '==', 'active'),
    orderBy('date', 'asc'),
  );
  const billsSnap = await getDocs(billsQuery);
  const bills = billsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  if (bills.length > MAX_BACKFILL_BILLS) {
    const e = new Error(
      `Müşteride çok fazla işlem var (${bills.length}). Tek seferde max ${MAX_BACKFILL_BILLS} işlenebilir.`,
    );
    e.code = 'too-many-bills';
    throw e;
  }

  const config = await getLoyaltyConfig(businessId);

  const result = await runTransaction(db, async (tx) => {
    const snap = await tx.get(customerRef);
    if (!snap.exists()) {
      const e = new Error('Müşteri bulunamadı');
      e.code = 'customer-not-found';
      throw e;
    }
    const loyalty = normalizeLoyalty(snap.data().loyalty);
    if (loyalty.backfillCompletedAt) {
      const e = new Error('Bu müşteri için backfill zaten tamamlanmış (concurrent)');
      e.code = 'already-backfilled';
      throw e;
    }

    let lifetimeSoFar = 0;
    let currentPointsRunning = 0;
    let totalEarned = 0;
    let expiredCount = 0;
    let expiredPoints = 0;
    let processedCount = 0;
    let skippedCount = 0;
    const now = new Date();
    const writes = [];

    for (const bill of bills) {
      const billTotalPrice = Number(bill.totalPrice) || 0;
      const billDate = parseBillDate(bill.date);

      if (billTotalPrice <= 0 || !billDate) {
        skippedCount += 1;
        continue;
      }

      const tierName = calculateTier(lifetimeSoFar, config);
      const tierCfg = getTierConfig(tierName, config);
      const earnPoints = calculateEarnPoints(billTotalPrice, tierName, config);

      if (earnPoints <= 0) {
        skippedCount += 1;
        continue;
      }

      lifetimeSoFar += earnPoints;
      totalEarned += earnPoints;
      processedCount += 1;

      const expiresAt = addMonthsToDate(billDate, config.expirationMonths);
      const isExpired = expiresAt < now;

      const earnTxRef = doc(collection(db, 'loyaltyTransactions'));
      if (!isExpired) currentPointsRunning += earnPoints;

      writes.push({ ref: earnTxRef, data: {
        customerId, billId: bill.id, type: TX_TYPES.EARN,
        points: earnPoints, remainingPoints: isExpired ? 0 : earnPoints,
        balanceAfter: currentPointsRunning, description: 'Backfill — geçmiş işlem',
        ruleSnapshot: {
          reason: 'backfill', billDate: Timestamp.fromDate(billDate),
          tier: tierName, multiplier: tierCfg.multiplier,
          earnRate: config.earnRate, totalPrice: billTotalPrice,
        },
        expiresAt: Timestamp.fromDate(expiresAt), reversedBy: null,
        businessId: businessId || null, createdAt: serverTimestamp(), createdBy: employeeId,
      }});

      if (isExpired) {
        expiredCount += 1;
        expiredPoints += earnPoints;
        const expireTxRef = doc(collection(db, 'loyaltyTransactions'));
        writes.push({ ref: expireTxRef, data: {
          customerId, billId: null, type: TX_TYPES.EXPIRE,
          points: -earnPoints, remainingPoints: 0, balanceAfter: currentPointsRunning,
          description: 'Backfill — süresi geçmiş puan',
          ruleSnapshot: {
            reason: 'backfill-expire', originalEarnId: earnTxRef.id,
            expiredAt: Timestamp.fromDate(expiresAt), originalPoints: earnPoints,
          },
          expiresAt: null, reversedBy: null, businessId: businessId || null,
          createdAt: serverTimestamp(), createdBy: employeeId,
        }});
      }
    }

    for (const w of writes) {
      tx.set(w.ref, w.data);
    }

    const finalTier = calculateTier(lifetimeSoFar, config);
    tx.update(customerRef, {
      'loyalty.currentPoints': currentPointsRunning,
      'loyalty.lifetimePoints': lifetimeSoFar,
      'loyalty.tier': finalTier,
      'loyalty.lastCalculatedAt': serverTimestamp(),
      'loyalty.backfillCompletedAt': serverTimestamp(),
      'loyalty.backfillBillCount': processedCount,
    });

    return {
      billCount: bills.length, processedCount, skippedCount, totalEarned,
      expiredCount, expiredPoints, finalCurrentPoints: currentPointsRunning,
      finalLifetimePoints: lifetimeSoFar, finalTier,
    };
  });

  return result;
};
